import { Component, ElementRef, ViewChild } from "@angular/core";
import { AccountFinderDialogComponent } from "app/admin/account-finder-dialog.component";
import { ActiveStatus, Addon, AddonDoc, AddonDocMap, AddonImage, AddonStation } from "app/model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";

import { EntityFinderDialog } from "app/shared/entity-finder.dialog";

import { DomainService } from "app/shared/domain.service";
import { FileSelectDialogComponent } from "app/shared/file-select-dialog.component";
import { FileViewDialogComponent, FileViewDialogParams, IDoc } from "app/shared/file-view-dialog.component";
import { ImageFns } from "app/shared/image/image-fns";
import { ImageHandlerComponent } from "app/shared/image/image-handler.component";
import { UtilFns } from "app/shared/util-fns";
import _ from "lodash";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { EditFeatureDialogParams } from "./edit-feature-dialog.component";

export interface EditAddonDialogParams extends DialogParams {
  // empty item means to add one.
  addon?: Addon;
}

@Component({
  templateUrl: "./edit-addon-dialog.component.html",
  standalone: false,
})
export class EditAddonDialogComponent extends DialogBaseComponent<EditAddonDialogParams> {
  @ViewChild(ImageHandlerComponent) imageHandler?: ImageHandlerComponent;
  @ViewChild("firstInput") firstInput: ElementRef<HTMLInputElement>;

  addon: Addon;
  activeStatuses: ActiveStatus[];
  addonStations: AddonStation[];
  isAdding: boolean;
  showAllErrors = false;
  groupingKeyCollisions: Addon[] = [];

  constructor(
    public config: DynamicDialogConfig<EditFeatureDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      height: "800px",
      width: "850px",
    });
    this.prepare();
  }

  getPngDialogService() {
    return this.domainService.pngDialogService;
  }

  private async prepare() {
    this.addon = this.params.addon;
    if (this.addon == null) {
      this.isAdding = true;
      this.addon = this.createNewAddonAsUnchanged();
    }
    this.config.header = this.isAdding ? "Add Add-On" : "Edit Add-On";
    this.activeStatuses = this.dbQueryService.getAllCached(ActiveStatus);
    this.addonStations = await this.dbQueryService.getAll(AddonStation);
    await this.checkGroupingKeyCollision();
    this.isPageReady = true;
    // Focus the first input after the view renders
    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  private createNewAddonAsUnchanged() {
    const addon = this.uow.createEntity(Addon, {
      accountId: null,
      addonStationId: null,
      activeStatusId: 1,
      groupingKey: this.generateRandomGroupingKey(),
    });
    addon.entityAspect.setUnchanged();
    // Don't call validateEntity() here - it would show errors before user input
    return addon;
  }

  /** Random 4-char uppercase alphanumeric, e.g. "K4Q9". Avoids accidental
   * batching collisions when the user doesn't override. */
  private generateRandomGroupingKey(): string {
    return Math.random().toString(36).substring(2, 6).toUpperCase();
  }

  /** Look up other addons (any account) sharing this Grouping Key at this station.
   * Drives the inline "Will batch with: ..." warning under the field. */
  async checkGroupingKeyCollision() {
    this.groupingKeyCollisions = await this.dbQueryService.getAddonsByGroupingKeyAndStation(this.addon);
  }

  groupingKeyCollisionSummary(): string {
    return this.groupingKeyCollisions
      .map((a) => {
        const acct = a.account?.accountName ? ` (${a.account.accountName})` : "";
        return `${a.nameAndLocation}${acct}`;
      })
      .join("; ");
  }

  async selectAccount() {
    const r = await EntityFinderDialog.open(AccountFinderDialogComponent, this.domainService, {});
    const account = _.first(r);
    if (account != null) {
      this.addon.account = account;
    }
  }

  async viewAddonDocs() {
    const data: FileViewDialogParams = {
      header: "View Addon Document",
      docs: this.addon.addonDocMaps.map((x) => x.addonDoc as IDoc),
      deleteFn: this.deleteAddonDoc.bind(this),
    };
    await DialogBaseComponent.open(FileViewDialogComponent, this, data);
  }

  async addAddonDoc() {
    const r = await DialogBaseComponent.open(FileSelectDialogComponent, this, {
      header: "Add Addon Doc",
      label: "Doc",
    });

    if (r) {
      const data = await ImageFns.blobToBase64(r);
      const aod = this.dbSaveService.createEntity(AddonDoc, {
        doc: data,
        fileName: r.name,
        mimeType: r.type,
        name: r.name,
        description: "n/a",
      });
      this.dbSaveService.createEntity(AddonDocMap, {
        addonId: this.addon.id,
        addonDocId: aod.id,
      });
    }
  }

  deleteAddonDoc(doc: AddonDoc) {
    const map = doc.addonDocMaps.find((x) => x.addon == this.addon);
    if (map) {
      map.entityAspect.setDeleted();
    }
    doc.entityAspect.setDeleted();
    return true;
  }

  /** Create the join entity between Image and parent entity */
  createImageJoinEntity(event: { imageId: number; isPrimary: boolean }): void {
    this.dbSaveService.uow.createEntity(AddonImage, {
      addonId: this.addon.id,
      imageId: event.imageId,
      isPrimary: event.isPrimary,
    });
  }

  async ok() {
    const ok = await this.save();
    if (!ok) {
      return;
    }
    this.dialogRef.close(true);
  }

  async save() {
    // Show all validation errors when user tries to save
    this.showAllErrors = true;

    if (this.isAdding) {
      this.addon.entityAspect.setAdded();
    }

    const isValid = await this.validate();
    if (!isValid) {
      return false;
    }

    await this.imageHandler?.attachFiles();

    try {
      const sr = await this.dbSaveService.saveChanges();
      return true;
    } catch (e) {
      return false;
    }
  }

  async validate() {
    let matchingEntity = await this.dbQueryService.getMatchingAddon(this.addon);
    if (matchingEntity != null) {
      await this.dialogService.showOkMessage("Duplicate Name", `Cannot save because this addon is a duplicate of an existing addon`);
      return false;
    }

    const ea = this.addon.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
      await this.dialogService.showOkMessage("Validation Error(s)", errMsg);
      return false;
    }
    return true;
  }

  async cancel() {
    // This insures that the rejectChanges below does not cause an html error on the page.
    this.isPageReady = false;
    if (this.isAdding) {
      this.addon.entityAspect.setDetached();
    } else {
      this.addon.entityAspect?.rejectChanges();
    }

    this.dialogRef.close(false);
  }
}
